
import React, { useState, useMemo, useEffect } from 'react';
import { Deal, DealStage, PipelineType, Owner, Property, Agency, UserProfile } from '../types';
import { MoreHorizontal, Plus, Calendar, ArrowRight, Wallet, Clock, Tag, Building, Briefcase, Key, AlertCircle, CheckCircle2, XCircle, Filter, TrendingUp, TrendingDown, DollarSign, LayoutGrid, List, Zap, Mail, Phone, MessageCircle, MessageSquare, FileText, Search, ArrowUpDown, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { AddDealModal } from './modals/AddDealModal';
import { DealDetailModal } from './modals/DealDetailModal';
import { SearchableSelect } from './ui/SearchableSelect';

interface PipelineProps {
  deals: Deal[];
  owners: Owner[];
  properties: Property[];
  agencies: Agency[];
  userProfile: UserProfile | null;
  setDeals: React.Dispatch<React.SetStateAction<Deal[]>>;
  onUpdateDeal: (deal: Deal) => void;
  onAddDeal: (deal: Deal, owner?: Owner, property?: Property) => Promise<void>;
  onUpdateOwner?: (owner: Owner) => Promise<void>;
  onUpdateProperty?: (property: Property) => Promise<void>;
  onDeleteDeal: (id: string) => void;
  initialDealId?: string;
  initialTab?: 'notes' | 'reminders' | 'sequence';
  onClearInitialDeal?: () => void;
  onNavigateToProposal?: (dealId: string) => void; 
  onNavigateToContract?: (dealId: string) => void; // Added prop
  onNavigateToManaged?: (propertyId: string) => void;
}

export const Pipeline: React.FC<PipelineProps> = ({ 
    deals, owners, properties, agencies, userProfile, setDeals, onUpdateDeal, onAddDeal, onUpdateOwner, onUpdateProperty, onDeleteDeal, initialDealId, initialTab, onClearInitialDeal, onNavigateToProposal, onNavigateToContract, onNavigateToManaged
}) => {
  type ProposalFilter = 'all' | 'with' | 'without';
  type SortMode = 'created_desc' | 'created_asc' | 'value_desc' | 'value_asc' | 'reminder_asc';
  const PIPELINE_PREFS_KEY = 'placid_pipeline_preferences_v1';
  const defaultPreferences = {
      filterType: 'ALL' as PipelineType | 'ALL',
      showClosed: false,
      stageFilter: 'all',
      sourceFilter: 'all',
      lateOnly: false,
      proposalFilter: 'all' as ProposalFilter,
      sortMode: 'created_desc' as SortMode
  };
  const readStoredPreferences = () => {
      if (typeof window === 'undefined') return defaultPreferences;
      try {
          const raw = window.localStorage.getItem(PIPELINE_PREFS_KEY);
          if (!raw) return defaultPreferences;
          const parsed = JSON.parse(raw);
          return { ...defaultPreferences, ...parsed };
      } catch {
          return defaultPreferences;
      }
  };
  const storedPreferences = readStoredPreferences();
  const [viewMode, setViewMode] = useState<'kanban' | 'calendar'>('kanban');
  const [filterType, setFilterType] = useState<PipelineType | 'ALL'>(storedPreferences.filterType);
  const [showClosed, setShowClosed] = useState(storedPreferences.showClosed);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState<string>(storedPreferences.stageFilter);
  const [sourceFilter, setSourceFilter] = useState<string>(storedPreferences.sourceFilter);
  const [lateOnly, setLateOnly] = useState(storedPreferences.lateOnly);
  const [proposalFilter, setProposalFilter] = useState<ProposalFilter>(storedPreferences.proposalFilter);
  const [sortMode, setSortMode] = useState<SortMode>(storedPreferences.sortMode);
  const [showStatsSection, setShowStatsSection] = useState(true);
  const [showFiltersSection, setShowFiltersSection] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [initialDetailTab, setInitialDetailTab] = useState<'notes' | 'reminders' | 'sequence'>('notes');

  // Écouter les messages de l'extension Chrome
  useEffect(() => {
      const processedMessages = new Set<string>();
      
      const handleMessage = (event: MessageEvent) => {
          if (event.data && event.data.type === 'PLACID_OPEN_DEAL_MODAL') {
              // Créer un identifiant unique pour éviter les doublons
              const messageId = `${event.data.url}-${event.data.html?.substring(0, 50)}`;
              
              if (processedMessages.has(messageId)) {
                  return; // Déjà traité
              }
              
              processedMessages.add(messageId);
              
              // Nettoyer après 5 secondes
              setTimeout(() => {
                  processedMessages.delete(messageId);
              }, 5000);
              
              // Ouvrir la modale pour créer un nouveau deal
              setIsAddModalOpen(true);
              // Relayer le message à la modale après un délai pour qu'elle soit montée
              // Augmenter le délai pour s'assurer que la modale est complètement montée
              setTimeout(() => {
                  window.postMessage({
                      type: 'PLACID_OPEN_DEAL_MODAL',
                      html: event.data.html,
                      url: event.data.url,
                      data: event.data.data
                  }, '*');
              }, 300);
          }
      };

      // Écouter les messages chrome.runtime si disponible
      if (typeof window !== 'undefined' && (window as any).chrome && (window as any).chrome.runtime && (window as any).chrome.runtime.onMessage) {
          (window as any).chrome.runtime.onMessage.addListener((request: any, sender: any, sendResponse: (response: any) => void) => {
              if (request.type === 'PLACID_OPEN_DEAL_MODAL') {
                  // Créer un identifiant unique pour éviter les doublons
                  const messageId = `${request.url}-${request.html?.substring(0, 50)}`;
                  
                  if (processedMessages.has(messageId)) {
                      sendResponse({ success: false, error: 'Already processed' });
                      return true;
                  }
                  
                  processedMessages.add(messageId);
                  
                  // Nettoyer après 5 secondes
                  setTimeout(() => {
                      processedMessages.delete(messageId);
                  }, 5000);
                  
                  setIsAddModalOpen(true);
                  // Relayer le message à la modale après un délai pour qu'elle soit montée
                  // Augmenter le délai pour s'assurer que la modale est complètement montée
                  setTimeout(() => {
                      window.postMessage({
                          type: 'PLACID_OPEN_DEAL_MODAL',
                          html: request.html,
                          url: request.url,
                          data: request.data
                      }, '*');
                  }, 300);
                  sendResponse({ success: true });
              }
              return true;
          });
      }

      // Écouter aussi les messages postMessage
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Handle Initial Deal from props (Navigation)
  useEffect(() => {
      if (initialDealId) {
          const deal = deals.find(d => d.id === initialDealId);
          if (deal) {
              setSelectedDeal(deal);
              if (initialTab) setInitialDetailTab(initialTab);
              if (onClearInitialDeal) onClearInitialDeal();
          }
      }
  }, [initialDealId, deals, initialTab, onClearInitialDeal]);

  // Sync selectedDeal with deals prop updates (Essential for refreshing data like comments/value from other views)
  useEffect(() => {
      if (selectedDeal) {
          const updatedDeal = deals.find(d => d.id === selectedDeal.id);
          // Update only if the object reference has changed (meaning data was refreshed)
          if (updatedDeal && updatedDeal !== selectedDeal) {
              setSelectedDeal(updatedDeal);
          }
      }
  }, [deals]);

  // Date Filtering State (Kanban Only)
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;
  const [dateRange, setDateRange] = useState({
      start: '2000-01-01', // Depuis toujours
      end: `${currentYear}-12-31` // Jusqu'à fin de l'année en cours
  });
  const [showStartInput, setShowStartInput] = useState(false);

  // Calendar State
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Drag & Drop State
  const [draggingDealId, setDraggingDealId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);

  // --- HELPERS ---
  const getOwnerName = (id: string) => {
    const owner = owners.find((o) => o.id === id);
    return owner ? `${owner.firstName} ${owner.lastName}` : 'Inconnu';
  };

  const getPropertyName = (id: string) => {
    const prop = properties.find((p) => p.id === id);
    return prop ? `${prop.type} ${prop.city}` : 'Bien en recherche';
  };

  const getAgencyCode = (id?: string | null) => {
      if (!id) return 'GLB';
      const agency = agencies.find(a => a.id === id);
      return agency ? agency.city.substring(0, 3).toUpperCase() : '---';
  };

  const getNextPendingReminder = (deal: Deal) => {
      return (deal.reminders || [])
          .filter((r) => !r.isDone)
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];
  };

  const getReminderTimestamp = (deal: Deal) => {
      const reminder = getNextPendingReminder(deal);
      if (!reminder) return null;
      return new Date(reminder.date).getTime();
  };

  const isDealLate = (deal: Deal) => {
      const reminderTs = getReminderTimestamp(deal);
      if (!reminderTs) return false;
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const reminderDate = new Date(reminderTs);
      reminderDate.setHours(0, 0, 0, 0);
      return reminderDate.getTime() < now.getTime();
  };

  const sourceOptions = useMemo(() => {
      return Array.from(
          new Set(
              deals
                  .map((d) => d.source)
                  .filter((source): source is string => Boolean(source && source.trim().length))
          )
      ).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [deals]);

  const stageSelectOptions = useMemo(
      () => [
          { value: 'all', label: 'Toutes les étapes' },
          ...Object.values(DealStage).map((stage) => ({ value: stage, label: stage }))
      ],
      []
  );

  const sourceSelectOptions = useMemo(
      () => [
          { value: 'all', label: 'Toutes les sources' },
          ...sourceOptions.map((source) => ({ value: source, label: source }))
      ],
      [sourceOptions]
  );

  const proposalSelectOptions = useMemo(
      () => [
          { value: 'all', label: 'Propositions: toutes' },
          { value: 'with', label: 'Avec proposition' },
          { value: 'without', label: 'Sans proposition' }
      ],
      []
  );

  const sortSelectOptions = useMemo(
      () => [
          { value: 'created_desc', label: 'Tri: plus récents' },
          { value: 'created_asc', label: 'Tri: plus anciens' },
          { value: 'value_desc', label: 'Tri: valeur décroissante' },
          { value: 'value_asc', label: 'Tri: valeur croissante' },
          { value: 'reminder_asc', label: 'Tri: rappel le plus proche' }
      ],
      []
  );

  useEffect(() => {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem(
          PIPELINE_PREFS_KEY,
          JSON.stringify({
              filterType,
              showClosed,
              stageFilter,
              sourceFilter,
              lateOnly,
              proposalFilter,
              sortMode
          })
      );
  }, [filterType, showClosed, stageFilter, sourceFilter, lateOnly, proposalFilter, sortMode]);

  const resetAdvancedControls = () => {
      setSearchQuery('');
      setStageFilter(defaultPreferences.stageFilter);
      setSourceFilter(defaultPreferences.sourceFilter);
      setLateOnly(defaultPreferences.lateOnly);
      setProposalFilter(defaultPreferences.proposalFilter);
      setSortMode(defaultPreferences.sortMode);
      setFilterType(defaultPreferences.filterType);
      setShowClosed(defaultPreferences.showClosed);
  };

  // --- FILTERING & STATS ---

  // 1. Filtered Deals for Columns (Visual Pipeline)
  // Logic: We show deals matching type/status/search/filters/sort (dates are only used for stats)
  const filteredDeals = useMemo(() => {
      const normalizedQuery = searchQuery.trim().toLowerCase();
      const base = deals.filter((deal) => {
          const typeMatch = filterType === 'ALL' || deal.type === filterType;
          const statusMatch = showClosed || (deal.stage !== DealStage.SIGNED && deal.stage !== DealStage.LOST);
          if (!typeMatch || !statusMatch) return false;
          if (stageFilter !== 'all' && deal.stage !== stageFilter) return false;
          if (sourceFilter !== 'all' && deal.source !== sourceFilter) return false;
          if (lateOnly && !isDealLate(deal)) return false;
          if (proposalFilter === 'with' && !deal.proposalId) return false;
          if (proposalFilter === 'without' && deal.proposalId) return false;

          if (!normalizedQuery) return true;

          const owner = owners.find((o) => o.id === deal.ownerId);
          const property = properties.find((p) => p.id === deal.propertyId);
          const haystack = [
              owner ? `${owner.firstName} ${owner.lastName}` : '',
              owner?.email || '',
              owner?.phone || '',
              property?.address || '',
              property?.city || '',
              deal.source || ''
          ]
              .join(' ')
              .toLowerCase();

          return haystack.includes(normalizedQuery);
      });

      const createdAtTs = (deal: Deal) => new Date(deal.createdAt || 0).getTime();
      return [...base].sort((a, b) => {
          switch (sortMode) {
              case 'created_asc':
                  return createdAtTs(a) - createdAtTs(b);
              case 'value_desc':
                  return (b.value || 0) - (a.value || 0);
              case 'value_asc':
                  return (a.value || 0) - (b.value || 0);
              case 'reminder_asc': {
                  const aReminder = getReminderTimestamp(a);
                  const bReminder = getReminderTimestamp(b);
                  if (aReminder === null && bReminder === null) return createdAtTs(b) - createdAtTs(a);
                  if (aReminder === null) return 1;
                  if (bReminder === null) return -1;
                  return aReminder - bReminder;
              }
              case 'created_desc':
              default:
                  return createdAtTs(b) - createdAtTs(a);
          }
      });
  }, [deals, filterType, showClosed, searchQuery, stageFilter, sourceFilter, lateOnly, proposalFilter, sortMode, owners, properties]);

  // 2. Stats Calculation (Based on Date Range & Filter Type, IGNORING showClosed toggle to always show accurate stats)
  const stats = useMemo(() => {
      const statsDeals = deals.filter((deal) => {
          const dealDate = new Date(deal.createdAt || new Date().toISOString());
          const start = new Date(dateRange.start);
          const end = new Date(dateRange.end);
          end.setHours(23, 59, 59, 999);

          const dateMatch = dealDate >= start && dealDate <= end;
          const typeMatch = filterType === 'ALL' || deal.type === filterType;
          return dateMatch && typeMatch;
      });

      const won = statsDeals.filter(d => d.stage === DealStage.SIGNED);
      const lost = statsDeals.filter(d => d.stage === DealStage.LOST);
      const active = statsDeals.filter(d => d.stage !== DealStage.SIGNED && d.stage !== DealStage.LOST);

      const wonAmount = won.reduce((acc, curr) => acc + (curr.value || 0), 0);
      const pipeAmount = active.reduce((acc, curr) => acc + (curr.value || 0), 0);

      return {
          wonCount: won.length,
          lostCount: lost.length,
          wonAmount,
          pipeAmount
      };
  }, [deals, dateRange, filterType]);

  const activeStages = Object.values(DealStage).filter(s => 
    showClosed ? true : (s !== DealStage.SIGNED && s !== DealStage.LOST)
  );

  const advanceDeal = (e: React.MouseEvent, deal: Deal) => {
      e.stopPropagation();
      const stageKeys = Object.values(DealStage);
      const currentIndex = stageKeys.indexOf(deal.stage);
      if (currentIndex < stageKeys.length - 1) {
          const nextStage = stageKeys[currentIndex + 1];
          onUpdateDeal({...deal, stage: nextStage});
      }
  };

  // --- DRAG AND DROP HANDLERS ---
  const handleDragStart = (e: React.DragEvent, dealId: string) => {
      e.dataTransfer.setData("dealId", dealId);
      e.dataTransfer.effectAllowed = "move";
      setDraggingDealId(dealId);
  };

  const handleDragOver = (e: React.DragEvent, stage: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (dragOverStage !== stage) setDragOverStage(stage);
  };

  const handleDrop = (e: React.DragEvent, stage: string) => {
      e.preventDefault();
      const dealId = e.dataTransfer.getData("dealId");
      if (dealId) {
          const deal = deals.find(d => d.id === dealId);
          if (deal && deal.stage !== stage) onUpdateDeal({ ...deal, stage: stage as DealStage });
      }
      setDraggingDealId(null);
      setDragOverStage(null);
  };

  const handleDragEnd = () => {
      setDraggingDealId(null);
      setDragOverStage(null);
  };

  // --- CALENDAR HELPERS ---
  const getDaysInMonth = (date: Date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const days = new Date(year, month + 1, 0).getDate();
      const firstDay = new Date(year, month, 1).getDay(); // 0 = Sun
      
      // Adjust for Monday start (0=Mon, 6=Sun)
      const firstDayAdjusted = firstDay === 0 ? 6 : firstDay - 1;
      
      const res = [];
      // Empty slots
      for(let i=0; i<firstDayAdjusted; i++) res.push(null);
      // Days
      for(let i=1; i<=days; i++) res.push(new Date(year, month, i));
      return res;
  };

  const getEventsForDay = (day: Date) => {
      const dayStr = day.toDateString();
      
      return filteredDeals.flatMap(d => {
          const events = [];
          
          // Reminders
          (d.reminders || []).forEach(r => {
              if (!r.isDone && new Date(r.date).toDateString() === dayStr) {
                  events.push({ 
                      id: r.id, 
                      deal: d, 
                      eventType: 'REMINDER', 
                      note: r.note,
                      date: r.date
                  });
              }
          });

          // Sequence Steps
          (d.sequenceSteps || []).forEach(s => {
              if (s.status === 'PENDING' && new Date(s.date).toDateString() === dayStr) {
                  events.push({ 
                      id: s.id, 
                      deal: d, 
                      eventType: 'SEQUENCE', 
                      note: s.title, 
                      subType: s.type,
                      date: s.date
                  }); 
              }
          });
          
          return events;
      }).sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const changeMonth = (offset: number) => {
      setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + offset, 1));
  };

  return (
    <div className="p-8 h-full flex flex-col">
      {/* HEADER & DASHBOARD */}
      <div className="mb-6 space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Pipeline</h2>
              <p className="text-slate-500 text-sm">Suivi commercial et performance</p>
            </div>
            
            <div className="flex flex-wrap gap-2 items-center bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
                <Calendar className="w-4 h-4 text-slate-400 ml-2" />
                {dateRange.start === '2000-01-01' && !showStartInput ? (
                    <span 
                        onClick={() => setShowStartInput(true)}
                        className="text-sm text-slate-600 w-32 cursor-pointer hover:text-amber-600"
                    >
                        Début
                    </span>
                ) : (
                    <input 
                        type="date" 
                        value={dateRange.start}
                        onChange={(e) => {
                            setDateRange({...dateRange, start: e.target.value});
                            if (e.target.value === '2000-01-01') {
                                setShowStartInput(false);
                            }
                        }}
                        onBlur={() => {
                            if (dateRange.start === '2000-01-01') {
                                setShowStartInput(false);
                            }
                        }}
                        className="text-sm border-none focus:ring-0 text-slate-600 bg-transparent w-32"
                        autoFocus={showStartInput}
                    />
                )}
                <span className="text-slate-300">-</span>
                <input 
                    type="date" 
                    value={dateRange.end}
                    onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                    className="text-sm border-none focus:ring-0 text-slate-600 bg-transparent w-32"
                />
                <div className="h-4 w-px bg-slate-200 mx-2"></div>
                <button 
                    onClick={() => setDateRange({ start: '2000-01-01', end: `${currentYear}-12-31` })}
                    className="text-xs font-bold text-amber-600 hover:bg-amber-50 px-2 py-1 rounded transition-colors"
                >
                    Global
                </button>
                <button 
                    onClick={() => setDateRange({ start: `${currentYear}-01-01`, end: `${currentYear}-12-31` })}
                    className="text-xs font-bold text-amber-600 hover:bg-amber-50 px-2 py-1 rounded transition-colors"
                >
                    Cette année
                </button>
                <button 
                    onClick={() => setDateRange({ start: `${lastYear}-01-01`, end: `${lastYear}-12-31` })}
                    className="text-xs font-bold text-amber-600 hover:bg-amber-50 px-2 py-1 rounded transition-colors"
                >
                    Année dernière
                </button>
            </div>
          </div>

          {/* STATS CARDS */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 md:p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Stats pipeline</p>
                <button
                    type="button"
                    onClick={() => setShowStatsSection((prev) => !prev)}
                    className="px-2.5 py-1 rounded-md border text-xs font-semibold text-slate-600 hover:bg-slate-50 inline-flex items-center"
                >
                    {showStatsSection ? <ChevronUp className="w-3 h-3 mr-1.5" /> : <ChevronDown className="w-3 h-3 mr-1.5" />}
                    {showStatsSection ? 'Masquer' : 'Afficher'}
                </button>
            </div>
            {showStatsSection && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-xl border border-green-100 shadow-sm flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-10"><CheckCircle2 className="w-12 h-12 text-green-500" /></div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Gagnés</p>
                    <div className="flex items-end space-x-2">
                        <span className="text-2xl font-bold text-green-600">{stats.wonCount}</span>
                        <span className="text-xs text-green-500 mb-1 flex items-center"><TrendingUp className="w-3 h-3 mr-1" /> Dossiers</span>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-red-100 shadow-sm flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-10"><XCircle className="w-12 h-12 text-red-500" /></div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Perdus</p>
                    <div className="flex items-end space-x-2">
                        <span className="text-2xl font-bold text-red-600">{stats.lostCount}</span>
                        <span className="text-xs text-red-500 mb-1 flex items-center"><TrendingDown className="w-3 h-3 mr-1" /> Dossiers</span>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-4 rounded-xl border border-slate-700 shadow-sm flex flex-col justify-between text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-10"><DollarSign className="w-12 h-12 text-white" /></div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">CA Sécurisé (Mensuel)</p>
                    <div className="flex items-end space-x-2">
                        <span className="text-2xl font-bold text-emerald-400">{stats.wonAmount.toLocaleString()} €</span>
                    </div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-amber-100 shadow-sm flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-2 opacity-10"><Wallet className="w-12 h-12 text-amber-500" /></div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Pipe Estimé (Mensuel)</p>
                    <div className="flex items-end space-x-2">
                        <span className="text-2xl font-bold text-amber-500">{stats.pipeAmount.toLocaleString()} €</span>
                        <span className="text-xs text-slate-400 mb-1">En cours</span>
                    </div>
                </div>
              </div>
            )}
          </div>
          
          {/* CONTROLS */}
          <div className="flex flex-wrap gap-2 justify-between items-center border-t border-slate-200 pt-4">
                <div className="flex items-center space-x-4">
                    {/* VIEW TOGGLE */}
                    <div className="bg-slate-100 p-1 rounded-lg flex">
                        <button onClick={() => setViewMode('kanban')} className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center transition-all ${viewMode === 'kanban' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'}`}>
                            <LayoutGrid className="w-3.5 h-3.5 mr-2" />
                            Kanban
                        </button>
                        <button onClick={() => setViewMode('calendar')} className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center transition-all ${viewMode === 'calendar' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'}`}>
                            <Calendar className="w-3.5 h-3.5 mr-2" />
                            Calendrier
                        </button>
                    </div>

                    <div className="h-6 w-px bg-slate-200"></div>

                    <div className="bg-slate-100 p-1 rounded-lg flex">
                        <button onClick={() => setFilterType('ALL')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filterType === 'ALL' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'}`}>Tout</button>
                        <button onClick={() => setFilterType(PipelineType.CONCIERGE)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filterType === PipelineType.CONCIERGE ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'}`}>Conciergerie</button>
                        <button onClick={() => setFilterType(PipelineType.SUBLET)} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filterType === PipelineType.SUBLET ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'}`}>Sous-location</button>
                    </div>
                </div>

                <div className="flex space-x-2">
                    <span className="text-xs text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full font-medium inline-flex items-center">
                        {filteredDeals.length} deal{filteredDeals.length > 1 ? 's' : ''} affiché{filteredDeals.length > 1 ? 's' : ''}
                    </span>
                    {viewMode === 'kanban' && (
                        <button onClick={() => setShowClosed(!showClosed)} className={`px-3 py-1.5 rounded-lg flex items-center border text-xs font-medium transition-colors ${showClosed ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}>
                            <Filter className="w-3 h-3 mr-2" />
                            {showClosed ? 'Masquer Archives' : 'Voir Archives'}
                        </button>
                    )}
                    <button onClick={() => setIsAddModalOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-1.5 rounded-lg flex items-center shadow-sm text-xs font-bold">
                        <Plus className="w-3 h-3 mr-2" /> Nouvelle Affaire
                    </button>
                </div>
           </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 md:p-4 space-y-3">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Recherche, filtres et tri</p>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={resetAdvancedControls}
                        className="text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2.5 py-1 rounded-md transition-colors inline-flex items-center"
                    >
                        <RotateCcw className="w-3 h-3 mr-1.5" />
                        Réinitialiser
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowFiltersSection((prev) => !prev)}
                        className="px-2.5 py-1 rounded-md border text-xs font-semibold text-slate-600 hover:bg-slate-50 inline-flex items-center"
                    >
                        {showFiltersSection ? <ChevronUp className="w-3 h-3 mr-1.5" /> : <ChevronDown className="w-3 h-3 mr-1.5" />}
                        {showFiltersSection ? 'Masquer' : 'Afficher'}
                    </button>
                </div>
            </div>
            {showFiltersSection && (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-2">
                <div className="relative xl:col-span-2">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Rechercher propriétaire, bien, source..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                </div>

                <SearchableSelect
                    options={stageSelectOptions}
                    value={stageFilter}
                    onChange={setStageFilter}
                    placeholder="Toutes les étapes"
                    icon={<Filter className="w-4 h-4" />}
                    showAllOnOpen={true}
                    className="text-sm"
                />

                <SearchableSelect
                    options={sourceSelectOptions}
                    value={sourceFilter}
                    onChange={setSourceFilter}
                    placeholder="Toutes les sources"
                    icon={<Tag className="w-4 h-4" />}
                    showAllOnOpen={true}
                    className="text-sm"
                />

                <SearchableSelect
                    options={proposalSelectOptions}
                    value={proposalFilter}
                    onChange={(value) => setProposalFilter(value as ProposalFilter)}
                    placeholder="Propositions"
                    icon={<FileText className="w-4 h-4" />}
                    showAllOnOpen={true}
                    className="text-sm"
                />

                <SearchableSelect
                    options={sortSelectOptions}
                    value={sortMode}
                    onChange={(value) => setSortMode(value as SortMode)}
                    placeholder="Tri"
                    icon={<ArrowUpDown className="w-4 h-4" />}
                    showAllOnOpen={true}
                    className="text-sm"
                />
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={() => setLateOnly((prev) => !prev)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors inline-flex items-center ${
                        lateOnly
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                >
                    <AlertCircle className="w-3.5 h-3.5 mr-1.5" />
                    Retard uniquement
                </button>
                <span className="text-xs text-slate-500 inline-flex items-center">
                    <ArrowUpDown className="w-3.5 h-3.5 mr-1" />
                    Le tri s’applique dans chaque colonne
                </span>
            </div>
            </>
            )}
          </div>
      </div>

      {/* Added bg-gray-50 to this container to ensure background covers scrolling area in dark mode */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden pb-4 bg-gray-50 rounded-xl">
        {viewMode === 'kanban' ? (
            /* --- KANBAN VIEW --- */
            <div className="flex h-full space-x-4 min-w-max p-2">
            {activeStages.map((stage) => {
                const stageDeals = filteredDeals.filter((d) => d.stage === stage);
                
                // Visual styles for Closed columns
                const isWonColumn = stage === DealStage.SIGNED;
                const isLostColumn = stage === DealStage.LOST;
                let headerColor = "bg-slate-100 border-slate-200";
                if (isWonColumn) headerColor = "bg-green-50 border-green-200";
                if (isLostColumn) headerColor = "bg-red-50 border-red-200";

                // Drag Over Visual
                const isDragOver = dragOverStage === stage;

                return (
                <div 
                    key={stage} 
                    onDragOver={(e) => handleDragOver(e, stage)}
                    onDrop={(e) => handleDrop(e, stage)}
                    className={`w-80 flex-shrink-0 flex flex-col rounded-xl max-h-full border transition-colors duration-200 
                        ${isWonColumn || isLostColumn ? 'bg-white' : 'bg-slate-100/80'} 
                        ${isDragOver ? 'ring-2 ring-amber-400 bg-amber-50 border-amber-300' : headerColor}
                    `}
                >
                    <div className={`p-3 border-b rounded-t-xl ${headerColor}`}>
                    <div className="flex justify-between items-center mb-1">
                        <h3 className={`font-semibold text-sm uppercase flex items-center ${isWonColumn ? 'text-green-800' : isLostColumn ? 'text-red-800' : 'text-slate-700'}`}>
                            {isWonColumn && <CheckCircle2 className="w-4 h-4 mr-1"/>}
                            {isLostColumn && <XCircle className="w-4 h-4 mr-1"/>}
                            {stage}
                            <span className="ml-2 bg-white/50 text-slate-900 text-[10px] px-2 py-0.5 rounded-full font-bold border border-slate-200/50">
                                {stageDeals.length}
                            </span>
                        </h3>
                    </div>
                    </div>

                    <div className="p-2 flex-1 overflow-y-auto space-y-2 custom-scrollbar">
                    {stageDeals.map((deal) => {
                        const now = new Date();
                        // Reset time for date-only comparison
                        now.setHours(0,0,0,0);
                        const nextReminder = deal.reminders?.filter(r => !r.isDone).sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0];    
                        const rDate = nextReminder ? new Date(nextReminder.date) : null;
                        if (rDate) rDate.setHours(0,0,0,0);
                        
                        const isLate = rDate ? rDate < now : false;
                        const isConcierge = deal.type === PipelineType.CONCIERGE;
                        const isDragging = draggingDealId === deal.id;

                        return (
                            <div 
                                key={deal.id}
                                draggable={!isWonColumn && !isLostColumn}
                                onDragStart={(e) => deal.id && handleDragStart(e, deal.id)}
                                onDragEnd={handleDragEnd}
                                onClick={() => { setSelectedDeal(deal); setInitialDetailTab('notes'); }}
                                className={`bg-white p-3 rounded-lg shadow-sm border border-slate-200 group relative hover:shadow-md transition-all cursor-pointer 
                                    ${isLate ? 'ring-2 ring-red-100 border-red-200' : ''}
                                    ${isDragging ? 'opacity-50 scale-95 rotate-1 shadow-xl ring-2 ring-amber-400' : ''}
                                    ${(!isWonColumn && !isLostColumn) ? 'hover:-translate-y-1' : ''}
                                `}
                            >
                                <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${isConcierge ? 'bg-blue-500' : 'bg-purple-500'}`}></div>
                                <div className="pl-2">
                                    <div className="flex justify-between items-start mb-1">
                                        <div className="flex items-center space-x-1 flex-wrap gap-y-1">
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border flex items-center uppercase tracking-tighter ${isConcierge ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'}`}>
                                                {isConcierge ? <Key className="w-2.5 h-2.5 mr-1" /> : <Briefcase className="w-2.5 h-2.5 mr-1" />}
                                                {isConcierge ? 'CONCIERGE' : 'SOUS-LOC'}
                                            </span>
                                            {(userProfile?.role === 'admin' || userProfile?.accessLevel === 'national') && deal.agencyId && (
                                                <span className="text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1 rounded flex items-center" title="Agence">
                                                    {getAgencyCode(deal.agencyId)}
                                                </span>
                                            )}
                                        </div>
                                        {deal.value > 0 && (
                                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${isWonColumn ? 'bg-green-100 text-green-700' : 'bg-emerald-50 text-emerald-600'}`}>
                                                {deal.value}€
                                            </span>
                                        )}
                                    </div>
                                    <h4 className="font-semibold text-slate-800 text-sm truncate leading-tight mt-2">
                                        {getOwnerName(deal.ownerId)}
                                    </h4>
                                    <p className="text-xs text-slate-500 mb-2 truncate">
                                        {getPropertyName(deal.propertyId)}
                                    </p>
                                    <div className="flex items-center gap-1 flex-wrap mb-2">
                                        {deal.source && (
                                            <span className="text-[10px] text-slate-500 bg-slate-50 border border-slate-100 px-1.5 rounded flex items-center">
                                                <Tag className="w-2.5 h-2.5 mr-1" /> {deal.source}
                                            </span>
                                        )}
                                        {deal.proposalId && (
                                            <span className="text-[10px] text-purple-600 bg-purple-50 border border-purple-100 px-1.5 rounded flex items-center font-bold">
                                                <FileText className="w-2.5 h-2.5 mr-1" /> Prop.
                                            </span>
                                        )}
                                    </div>
                                    {nextReminder && !isWonColumn && !isLostColumn ? (
                                        <div className={`mt-2 p-1.5 rounded text-xs flex items-center ${isLate ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                                            {isLate ? <AlertCircle className="w-3 h-3 mr-1.5 flex-shrink-0" /> : <Clock className="w-3 h-3 mr-1.5 flex-shrink-0" />}
                                            <span className="truncate font-medium">
                                                {isLate ? 'En retard : ' : ''}{new Date(nextReminder.date).toLocaleDateString()} - {nextReminder.note}
                                            </span>
                                        </div>
                                    ) : null}
                                    <div className="flex items-center justify-between text-[10px] text-slate-400 pt-2 border-t border-slate-50 mt-2">
                                        <div className="flex items-center">
                                            <Calendar className="w-3 h-3 mr-1" />
                                            {new Date(deal.createdAt || '').toLocaleDateString()}
                                        </div>
                                        {!isWonColumn && !isLostColumn && (
                                            <button onClick={(e) => advanceDeal(e, deal)} className="opacity-0 group-hover:opacity-100 transition-opacity bg-slate-100 hover:bg-slate-200 text-slate-600 p-1 rounded hover:text-amber-600" title="Étape suivante">
                                                <ArrowRight className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                    )})}
                    {!isWonColumn && !isLostColumn && (
                        <button onClick={() => setIsAddModalOpen(true)} className="w-full py-2 text-sm text-slate-500 hover:bg-slate-200/50 rounded-lg border border-dashed border-slate-300 flex items-center justify-center transition-colors">
                            <Plus className="w-4 h-4 mr-1" /> Ajouter
                        </button>
                    )}
                    </div>
                </div>
                );
            })}
            </div>
        ) : (
            /* --- CALENDAR VIEW --- */
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 h-full flex flex-col">
                {/* Cal Header */}
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-bold text-lg text-slate-800 capitalize">
                        {calendarDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                    </h3>
                    <div className="flex space-x-2">
                        <button onClick={() => changeMonth(-1)} className="p-1 hover:bg-slate-100 rounded text-slate-500">
                            Précédent
                        </button>
                         <button onClick={() => setCalendarDate(new Date())} className="p-1 px-3 text-xs bg-amber-100 text-amber-700 rounded font-bold">
                            Aujourd'hui
                        </button>
                        <button onClick={() => changeMonth(1)} className="p-1 hover:bg-slate-100 rounded text-slate-500">
                            Suivant
                        </button>
                    </div>
                </div>

                {/* Cal Grid */}
                <div className="flex-1 grid grid-cols-7 grid-rows-[auto_1fr] overflow-hidden">
                    {/* Weekday Headers */}
                    {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                        <div key={day} className="p-2 text-center text-xs font-bold text-slate-500 border-b border-r border-slate-100 bg-slate-50 last:border-r-0">
                            {day}
                        </div>
                    ))}

                    {/* Days */}
                    {getDaysInMonth(calendarDate).map((day, idx) => {
                        const events = day ? getEventsForDay(day) : [];
                        const isToday = day && day.toDateString() === new Date().toDateString();

                        return (
                            <div key={idx} className={`border-b border-r border-slate-100 p-2 overflow-y-auto relative ${!day ? 'bg-slate-50/50' : 'bg-white'} ${idx % 7 === 6 ? 'border-r-0' : ''}`}>
                                {day && (
                                    <>
                                        <div className={`text-xs font-bold mb-1 ${isToday ? 'text-amber-600 bg-amber-100 w-6 h-6 flex items-center justify-center rounded-full' : 'text-slate-400'}`}>
                                            {day.getDate()}
                                        </div>
                                        <div className="space-y-1">
                                            {events.map((evt, i) => {
                                                const isSequence = evt.eventType === 'SEQUENCE';
                                                // Date only comparison
                                                const evtDate = new Date(evt.date);
                                                evtDate.setHours(0,0,0,0);
                                                const todayMidnight = new Date();
                                                todayMidnight.setHours(0,0,0,0);
                                                const isLate = evtDate < todayMidnight;
                                                
                                                let icon = null;
                                                if (isSequence) {
                                                    if(evt.subType === 'EMAIL') icon = <Mail className="w-3 h-3 mr-1" />;
                                                    else if(evt.subType === 'CALL') icon = <Phone className="w-3 h-3 mr-1" />;
                                                    else if(evt.subType === 'WHATSAPP') icon = <MessageCircle className="w-3 h-3 mr-1" />;
                                                    else if(evt.subType === 'SMS') icon = <MessageSquare className="w-3 h-3 mr-1" />;
                                                    else icon = <Zap className="w-3 h-3 mr-1" />;
                                                }

                                                return (
                                                    <div 
                                                        key={i} 
                                                        onClick={() => {
                                                            setSelectedDeal(evt.deal);
                                                            setInitialDetailTab(isSequence ? 'sequence' : 'reminders');
                                                        }}
                                                        className={`text-[10px] p-1 rounded cursor-pointer truncate border flex items-center ${isLate ? 'bg-red-50 border-red-100 text-red-700' : isSequence ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-blue-50 border-blue-100 text-blue-700'} hover:opacity-80`}
                                                        title={`${evt.note}`}
                                                    >
                                                        {icon}
                                                        {evt.note}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        )}
      </div>
      
      <AddDealModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={onAddDeal} owners={owners} properties={properties} agencies={agencies} userProfile={userProfile} />
      {selectedDeal && (
          <DealDetailModal 
            isOpen={!!selectedDeal} onClose={() => setSelectedDeal(null)} deal={selectedDeal} owner={owners.find(o => o.id === selectedDeal.ownerId)!} property={selectedDeal.propertyId ? properties.find(p => p.id === selectedDeal.propertyId) || null : null} agency={agencies.find(a => a.id === selectedDeal.agencyId)} userRole={userProfile?.role || 'user'}
            userProfile={userProfile}
            onUpdate={(updatedDeal) => { onUpdateDeal(updatedDeal); setSelectedDeal(updatedDeal); }}
            onUpdateOwner={onUpdateOwner}
            onUpdateProperty={onUpdateProperty}
            onDelete={() => { if (window.confirm("Êtes-vous sûr ?")) { onDeleteDeal(selectedDeal.id!); setSelectedDeal(null); }}}
            initialTab={initialDetailTab}
            onNavigateToManaged={(id) => {
                if(onNavigateToManaged) {
                    onNavigateToManaged(id);
                    setSelectedDeal(null);
                }
            }}
            onGenerateProposal={() => {
                if (onNavigateToProposal && selectedDeal.id) {
                    onNavigateToProposal(selectedDeal.id);
                    setSelectedDeal(null); // Close modal
                }
            }}
            onNavigateToContract={() => {
                if (onNavigateToContract && selectedDeal.id) {
                    onNavigateToContract(selectedDeal.id);
                    setSelectedDeal(null);
                }
            }}
          />
      )}
    </div>
  );
};
